import {FetchResult, ServerError} from '@logistics-frontend/core/utils/network'
import {delay, equals, findLast, flow, get} from '@logistics-frontend/utils'

import {yaAuthFetch} from 'src/utils'

import {toFeedbackRequestBody} from './mappers'
import * as t from './types'

const NIL = '00000000-0000-0000-0000-000000000000'
const getBody = get('body')

const withCargoC2CAPIPath = (endpoint: string) => `/4.0/cargo-c2c/v1/shared-route${endpoint}`

export const fetchSharedRouteInfo = (body: t.SharedRouteKey): Promise<FetchResult<t.SharedRouteInfoResponse>> => {
  if (process.env.NODE_ENV !== 'production') {
    if (body.key === NIL) {
      return import('src/utils/devMocks').then(r => r.sharingMock)
    }
  }

  return yaAuthFetch({
    url: withCargoC2CAPIPath('/info'),
    body,
    method: 'POST',
  })
}

export const fetchPerformerPosition = (body: t.SharedRouteKey): Promise<t.PerformerPositionResponseWithDirection> => {
  if (process.env.NODE_ENV !== 'production') {
    if (body.key === NIL) {
      return import('src/utils/devMocks').then(r => r.performerMock).then(getBody)
    }
  }

  return yaAuthFetch<t.SharedRouteKey, t.PerformerPositionResponseWithDirection>({
    url: withCargoC2CAPIPath('/performer-position'),
    body,
    method: 'POST',
    retries: 0,
  }).then(getBody)
}

export const fetchPhoneByVoiceForwarding = (body: t.SharedRouteVoiceforwardingRequest): Promise<t.Voiceforwarding> =>
  yaAuthFetch<t.SharedRouteVoiceforwardingRequest, t.Voiceforwarding>({
    url: withCargoC2CAPIPath('/voiceforwarding'),
    body,
    method: 'POST',
  }).then(getBody)

export const fetchSendFeedback = (userFeedback: t.UserFeedback, sharingKey: string): Promise<unknown> =>
  yaAuthFetch<t.SharedRouteDeliveryFeedbackRequest, unknown>({
    url: withCargoC2CAPIPath('/feedback'),
    body: toFeedbackRequestBody(userFeedback, sharingKey),
    method: 'POST',
    idempotent: true,
  })

export const fetchSharedRoutePoll = (sharingKey: string, answersChoices: t.QuestionChoice[]): Promise<unknown> =>
  yaAuthFetch({
    url: withCargoC2CAPIPath('/poll'),
    body: {key: sharingKey, answers_choices: answersChoices},
    method: 'POST',
    idempotent: true,
  })

function fetchCancel(body: t.SharedRouteCancelRequest): Promise<t.OperationIdResponse> {
  // return import('src/__mocks__/shared-route-cancel-paid/cancel').then(r => r.default(body))
  return yaAuthFetch<t.SharedRouteCancelRequest, t.OperationIdResponse>({
    url: withCargoC2CAPIPath('/cancel'),
    body,
    method: 'post',
    idempotent: true,
  }).then(getBody)
}

export function fetchEvents(body: t.SharedRouteEventsRequest): Promise<t.SharedRouteEventsResponse> {
  // return import('src/__mocks__/shared-route-cancel-paid/events').then(r => r.default(body))
  return yaAuthFetch<t.SharedRouteEventsRequest, t.SharedRouteEventsResponse>({
    url: withCargoC2CAPIPath('/events'),
    body,
    method: 'post',
  }).then(getBody)
}

const getCancelSucceeded: (response: t.SharedRouteEventsResponse) => t.OrderCancelSucceeded | undefined = flow(
  get('events'),
  findLast<t.OrderCancelSucceeded>(flow(get('type'), equals('order-cancel-succeeded'))),
)
const getCancelFailed: (response: t.SharedRouteEventsResponse) => t.OrderCancelFailed | undefined = flow(
  get('events'),
  findLast<t.OrderCancelFailed>(flow(get('type'), equals('order-cancel-failed'))),
)

export function fetchSharedRouteCancel(body: t.CancelBody): Promise<void> {
  const {key} = body

  async function fetchEventsPolling(revision: number): Promise<void> {
    const response = await fetchEvents({key, revision})
    const succeeded = getCancelSucceeded(response)
    const failed = getCancelFailed(response)

    if (succeeded) {
      return
    }

    if (failed && failed.message) {
      throw new ServerError<t.CancelPaidError>({
        body: {
          message: failed.message,
          revision: response.revision,
        },
        status: 409,
        // @ts-expect-error кривая типизация у ошибки
        error: new Error(failed.message.title),
      })
    }

    return delay(1000).then(() => fetchEventsPolling(response.revision))
  }

  return fetchCancel({key, cancel_type: body.type})
    .then(() => delay(1000))
    .then(() => fetchEventsPolling(body.revision))
}

export function fetchPromotions(body: t.PromotionsRequest): Promise<FetchResult<t.PromotionsResponse>> {
  // return import('src/__mocks__/delivery-promotions').then(r => r.default)

  return yaAuthFetch({
    url: withCargoC2CAPIPath('/v1/delivery-promotions'),
    method: 'post',
    body,
  })
}
